import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AttendeeResponse } from 'src/app/features/video-conference/chime/models/AttendeeResponse';
import { MeetingResponse } from 'src/app/features/video-conference/chime/models/MeetingResponse';
import { Store } from '@ngrx/store';
import { SignalRService } from 'src/app/features/signal-r';
import { RootStoreState } from 'src/app/store';
import { ChimeActions } from 'src/app/features/video-conference/chime/store';
import { RecordingStatus } from 'src/app/features/video-conference/chime/enums/recording-status-modals.enum';
import { delayedRetry } from 'src/app/operators';

@Injectable({ providedIn: 'root' })
export class ChimeService {
  private listenerConfigured = false;
  private pendingNonInProgressTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly signalRService: SignalRService,
    private readonly store: Store<RootStoreState.State>
  ) {}

  // Meeting
  createMeeting(): Observable<MeetingResponse> {
    return this.httpClient.post<MeetingResponse>(`videoConference`, {}).pipe(delayedRetry(1000, 3));
  }

  getMeeting(): Observable<MeetingResponse> {
    return this.httpClient.get<MeetingResponse>(`videoConference`).pipe(delayedRetry(1000, 3));
  }

  stopMeetingAndRecording(): Observable<any> {
    return this.httpClient.delete<any>(`videoConference`).pipe(delayedRetry(1000, 3));
  }

  // Attendee
  createAttendee(meetingId: string): Observable<AttendeeResponse> {
    return this.httpClient.post<AttendeeResponse>(`videoConference/attendee`, {
      meetingId: meetingId,
    }).pipe(delayedRetry(1000, 3));
  }

  getAttendee(): Observable<AttendeeResponse> {
    return this.httpClient.get<AttendeeResponse>(`videoConference/attendee`).pipe(delayedRetry(1000, 3));
  }

  // Recording
  startRecording(): Observable<any> {
    return this.httpClient.post<any>(`videoConferenceRecording`, {}).
      pipe(delayedRetry(1000, 3));
  }

  getRecordingStatus(): Observable<any> {
    return this.httpClient.get<any>(`videoConferenceRecording/status`, {}).
      pipe(delayedRetry(1000, 3));
  }

  public configureListeners() {
    if (!this.listenerConfigured) {
      this.listenerConfigured = true;

      this.signalRService.hubConnection.on(
        'ReceiveVideoConferenceRecordingStatusUpdated',
        (data) => {
          const recordingStatus =
            RecordingStatus[data.mediaRecordingStatus as keyof typeof RecordingStatus] ??
            RecordingStatus.Unknown;

          if (recordingStatus === RecordingStatus.InProgress) {
            // Positive signals dispatch immediately — cancel any pending non-InProgress transition
            if (this.pendingNonInProgressTimeout) {
              clearTimeout(this.pendingNonInProgressTimeout);
              this.pendingNonInProgressTimeout = null;
            }
            this.store.dispatch(
              ChimeActions.SetRecordingStatus({
                payload: { recordingStatus: recordingStatus },
              })
            );
          } else {
            // Debounce transitions away from InProgress to avoid false "stopped recording"
            // from stale or out-of-order events. If an InProgress event arrives within the
            // window, the pending transition is cancelled.
            if (!this.pendingNonInProgressTimeout) {
              this.pendingNonInProgressTimeout = setTimeout(() => {
                this.pendingNonInProgressTimeout = null;
                this.store.dispatch(
                  ChimeActions.SetRecordingStatus({
                    payload: { recordingStatus: recordingStatus },
                  })
                );
              }, 3000);
            }
          }
        }
      );
    }
  }

  // Client Logs
  sendWarningLog(message: string): Observable<any> {
    return this.httpClient.post<any>(`videoConferenceMonitoring/sendChimeClientWarningLog`, {
      message: message,
    });
  }

  sendErrorLog(message: string): Observable<any> {
    return this.httpClient.post<any>(`videoConferenceMonitoring/sendChimeClientErrorLog`, {
      message: message,
    });
  }

  // Send captured frame to backend
  sendCapturedFrame(frame: string): Observable<any> {
    return this.httpClient.post<any>(`frameCapture`, {
      base64CapturedFrame: frame,
    });
  }
}
